import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useState, type MouseEvent } from 'react'
import type { IInstrumentTemplateSummary } from '@brushfire/types'

type Props = Readonly<{
  template: IInstrumentTemplateSummary
  onHide: (template: IInstrumentTemplateSummary) => void
  onUnhide: (template: IInstrumentTemplateSummary) => void
}>

export function PlatformTemplateTileMenu({ template, onHide, onUnhide }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const hidden = Boolean(template.hidden)

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleAction = () => {
    handleClose()
    if (hidden) {
      onUnhide(template)
    } else {
      onHide(template)
    }
  }

  return (
    <>
      <IconButton
        aria-label={hidden ? 'Unhide template menu' : 'Hide template menu'}
        data-testid={`platform-template-menu-${template.slug}`}
        onClick={handleOpen}
        size="small"
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          width: 16,
          height: 16,
          p: 0,
          color: '#64748b',
        }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        data-testid={`platform-template-menu-panel-${template.slug}`}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              width: hidden ? 142 : 130,
              mt: 0.5,
              borderRadius: '10px',
              border: '0.5px solid #e0e5eb',
              bgcolor: '#ffffff',
              boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
              '& .MuiList-root': {
                py: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          onClick={handleAction}
          data-testid={
            hidden ? `platform-template-unhide-${template.slug}` : `platform-template-hide-${template.slug}`
          }
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            color: '#23262f',
            letterSpacing: '-0.1504px',
            minHeight: 31,
            px: '18px',
            py: '6px',
            justifyContent: 'flex-start',
            '&:hover': {
              bgcolor: '#f8fafc',
            },
          }}
        >
          {hidden ? 'Unhide Template' : 'Hide Template'}
        </MenuItem>
      </Menu>
    </>
  )
}
